<template>
    <div>
        <div class="mb-2" v-if="label">
            <label class="v-label">{{ label }}</label>
        </div>
        <div class="control-group">
            <div class="button-group">
                <v-btn
                    @click="editor.chain().focus().toggleBold().run()"
                    :disabled="!editor.can().chain().focus().toggleBold().run()"
                    :color="editor.isActive('bold') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatBold</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleItalic().run()"
                    :disabled="!editor.can().chain().focus().toggleItalic().run()"
                    :color="editor.isActive('italic') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatItalic</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleStrike().run()"
                    :disabled="!editor.can().chain().focus().toggleStrike().run()"
                    :color="editor.isActive('strike') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatStrikethroughVariant</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleUnderline().run()"
                    :color="editor.isActive('underline') ? 'primary' : 'default'"
                    class="mr-5"
                    tile
                    icon
                >
                    <v-icon>$formatUnderline</v-icon>
                </v-btn>

                <v-btn
                    @click="editor.chain().focus().setTextAlign('left').run()"
                    :color="editor.isActive({ textAlign: 'left' }) ? 'primary': 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatAlignLeft</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().setTextAlign('center').run()"
                    :color="editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatAlignCenter</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().setTextAlign('right').run()"
                    :color="editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatAlignRight</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().setTextAlign('justify').run()"
                    :color="editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatAlignJustify</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleBulletList().run()"
                    :color="editor.isActive('bulletList') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatListBulleted</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleOrderedList().run()"
                    :color="editor.isActive('orderedList') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatListNumbered</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().indent().run()"
                    :color="editor.isActive('bulletList') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatIndentIncrease</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().outdent().run()"
                    :color="editor.isActive('orderedList') ? 'primary' : 'default'"
                    class="mr-5"
                    tile
                    icon
                >
                    <v-icon>$formatIndentDecrease</v-icon>
                </v-btn>

                <v-btn
                    @click="editor.chain().focus().setParagraph().run()"
                    :color="editor.isActive('paragraph') ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatParagraph</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                    :color="editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatHeader1</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                    :color="editor.isActive('heading', { level: 2 }) ? 'primary': 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatHeader2</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                    :color="editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatHeader3</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
                    :color="editor.isActive('heading', { level: 4 }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatHeader4</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
                    :color="editor.isActive('heading', { level: 5 }) ? 'primary' : 'default'"
                    tile
                    icon
                >
                    <v-icon>$formatHeader5</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
                    :color="editor.isActive('heading', { level: 6 }) ? 'primary' : 'default'"
                    class="mr-5"
                    tile
                    icon
                >
                    <v-icon>$formatHeader6</v-icon>
                </v-btn>

                <v-btn
                    @click="clearFormat"
                    tile
                    icon
                >
                    <v-icon>$formatClear</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().undo().run()"
                    :disabled="!editor.can().chain().focus().undo().run()"
                    tile
                    icon
                >
                    <v-icon>$undo</v-icon>
                </v-btn>
                <v-btn
                    @click="editor.chain().focus().redo().run()"
                    :disabled="!editor.can().chain().focus().redo().run()"
                    tile
                    icon
                >
                    <v-icon>$redo</v-icon>
                </v-btn>
            </div>
        </div>
        <div class="editor-container">
            <editor-content :editor="editor" />
        </div>
        <div v-if="message" class="v-text-field__details mt-2">
            <div :class="[message.error ? 'error--text' : '', 'v-messages theme--light']" >
                <div class="v-messages__wrapper">
                    <div class="v-messages__message">
                        {{ message.text }}
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { Indent } from '@mandelli/tiptap-extension-indent'
import {
    CustomStyleNode,
    CustomDivNode,
    CustomImgNode,
    CustomTableNode,
    CustomSpanNode
} from './tiptap-nodes'

export default {
    components: {
        EditorContent,
    },

    props: {
        value: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        hint: {
            type: String,
            default: '',
        },
        errorMessages: {
            type: Array,
            default: () => [],
        },
    },

    data: () => ({
        editor: null,
    }),

    watch: {
        value(value) {
            // HTML
            const isSame = this.editor.getHTML() === value

            // JSON
            // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

            if (isSame) {
                return
            }

            this.editor.commands.setContent(value, false)
        },
    },

    computed: {
        message() {
            let message = {
                text: this.hint,
                error: false
            }

            if (this.errorMessages.length > 0) {
                message = {
                    text: this.errorMessages.join(' '),
                    error: true
                }
            }

            return message
        },
    },

    created() {
        this.editor = new Editor({
            content: this.value,
            extensions: [
                CustomDivNode,
                CustomStyleNode,
                CustomImgNode,
                CustomTableNode,
                CustomSpanNode,
                StarterKit,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                Indent,
                Underline,
            ],
            editorProps: {
                attributes: {
                    class: 'white elevation-2 ma-8 px-5 py-6 outline-none',
                },
            },
            onUpdate: () => {
                // HTML
                this.$emit('input', this.editor.getHTML())

                // JSON
                // this.$emit('input', this.editor.getJSON())
            },
        })
    },

    methods: {
        clearFormat() {
            this.editor.chain().focus().unsetAllMarks().run()
            this.editor.chain().focus().clearNodes().run()
        },

        isDisabled(method) {
            return !this.editor.can().chain().focus()[method]().run()
        },
    },

    beforeDestroy() {
        this.editor.destroy()
    },
}
</script>

<style lang="scss" scoped>
.control-group {
    border: 1px solid var(--v-neutral-2-base) !important;
    padding: 6px 10px;

    .button-group {
        text-align: center;
    }
}

/* Basic editor styles */
.tiptap {
    :first-child {
        margin-top: 0;
    }

    /* List styles */
    ul,
    ol {
        padding: 0 1rem;
        margin: 1.25rem 1rem 1.25rem 0.4rem;

        li p {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
        }
    }

    /* Heading styles */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        line-height: 1.1;
        margin-top: 2.5rem;
        text-wrap: pretty;
    }

    h1,
    h2 {
        margin-top: 3.5rem;
        margin-bottom: 1.5rem;
    }

    h1 {
        font-size: 1.4rem;
    }

    h2 {
        font-size: 1.2rem;
    }

    h3 {
        font-size: 1.1rem;
    }

    h4,
    h5,
    h6 {
        font-size: 1rem;
    }

    /* Code and preformatted text styles */
    code {
        background-color: var(--purple-light);
        border-radius: 0.4rem;
        color: var(--black);
        font-size: 0.85rem;
        padding: 0.25em 0.3em;
    }

    pre {
        background: var(--black);
        border-radius: 0.5rem;
        color: var(--white);
        font-family: 'JetBrainsMono', monospace;
        margin: 1.5rem 0;
        padding: 0.75rem 1rem;

        code {
        background: none;
        color: inherit;
        font-size: 0.8rem;
        padding: 0;
        }
    }

    blockquote {
        border-left: 3px solid var(--gray-3);
        margin: 1.5rem 0;
        padding-left: 1rem;
    }

    hr {
        border: none;
        border-top: 1px solid var(--gray-2);
        margin: 2rem 0;
    }
}

.editor-container {
    border: 1px solid var(--v-neutral-2-base) !important;
    background-color: rgb(235, 230, 212);
    max-height: 600px;
    overflow-y: auto;
}
</style>

<style lang="scss">
    .ProseMirror:focus {
        outline: none;
    }

    @for $i from 1 through 8 {
        .tt-indent-#{$i} {
            $val: $i * 40px;
            padding-left: $val;
        }
    }
</style>